export enum Stage {
  TEST = 'test', // For local development
  BETA = 'beta',
  GAMMA = 'gamma',
  PROD = 'prod',
}

export enum ServiceType {
  ORBIT = 'ORBIT',
  HELIOS = 'HELIOS',
  STARLIGHT = 'STARLIGHT',
}

/**
 * Lowercase service key accepted by {@link getAuthUrl} and the session warm-up
 * flows (`AuthHandler`, `useServiceSessions`). Keep in sync with {@link ServiceType}
 * when adding a service.
 */
export type AuthService = 'orbit' | 'helios' | 'starlight';

// Use KatalWebpackPlugin template replacement, default to beta for tests
export const CURRENT_STAGE = (
  process.env.NODE_ENV === 'test' ? 'beta' : 'beta'.toLowerCase()
) as Stage;

const SERVICE_URL_MAP: Record<Stage, Record<ServiceType, string>> = {
  [Stage.TEST]: {
    [ServiceType.ORBIT]: 'https://orbit-public.us-west-2.beta.glmspa.amzl.amazon.dev',
    [ServiceType.HELIOS]: 'https://helios-public.us-west-2.beta.glmspa.amzl.amazon.dev',
    [ServiceType.STARLIGHT]:
      'https://starlight-public.us-west-2.beta.glmspa.amzl.amazon.dev',
  },
  [Stage.BETA]: {
    [ServiceType.ORBIT]: 'https://orbit-public.us-west-2.beta.glmspa.amzl.amazon.dev',
    [ServiceType.HELIOS]: 'https://helios-public.us-west-2.beta.glmspa.amzl.amazon.dev',
    [ServiceType.STARLIGHT]:
      'https://starlight-public.us-west-2.beta.glmspa.amzl.amazon.dev',
  },
  [Stage.GAMMA]: {
    [ServiceType.ORBIT]: 'https://orbit-public.us-west-2.gamma.glmspa.amzl.amazon.dev',
    [ServiceType.HELIOS]: 'https://helios-public.us-west-2.gamma.glmspa.amzl.amazon.dev',
    [ServiceType.STARLIGHT]:
      'https://starlight-public.us-west-2.gamma.glmspa.amzl.amazon.dev',
  },
  [Stage.PROD]: {
    [ServiceType.ORBIT]: 'https://orbit-public.us-west-2.prod.glmspa.amzl.amazon.dev',
    [ServiceType.HELIOS]: 'https://helios-public.us-west-2.prod.glmspa.amzl.amazon.dev',
    [ServiceType.STARLIGHT]:
      'https://starlight-public.us-west-2.prod.glmspa.amzl.amazon.dev',
  },
};

export const API_VERSION = {
  V1: '/v1',
  V2: '/v2',
} as const;

export const baseUrl = (serviceType: ServiceType): string => {
  return SERVICE_URL_MAP[CURRENT_STAGE][serviceType];
};

/**
 * Creates an API URL with the appropriate version prefix
 */
export const createApiUrl = (endpoint: string): string => {
  return `${API_VERSION.V1}${endpoint}`;
};

export const getAuthUrl = (service: AuthService): string => {
  const serviceType = service.toUpperCase() as ServiceType;
  return `${SERVICE_URL_MAP[CURRENT_STAGE][serviceType]}/ping`;
};

export const API_ENDPOINTS = {
  ORBIT_SERVERICE: {
    USERS: {
      ALL: (programId: string) => `/programs/${encodeURIComponent(programId)}/users`,
      BY_ID: (programId: string, userId: string) =>
        `/programs/${encodeURIComponent(programId)}/users/${encodeURIComponent(userId)}`,
      CREATE: (programId: string) => `/programs/${encodeURIComponent(programId)}/users`,
      UPDATE: (programId: string, userId: string) =>
        `/programs/${encodeURIComponent(programId)}/users/${encodeURIComponent(userId)}`,
      DELETE: (programId: string, userId: string) =>
        `/programs/${encodeURIComponent(programId)}/users/${encodeURIComponent(userId)}`,

      CHECK_USER_PERMISSION: (programId: string, userId: string) =>
        `/programs/${encodeURIComponent(programId)}/users/${encodeURIComponent(userId)}/permissions/check`,
      METADATA: (programId: string, userId: string) =>
        `/programs/${encodeURIComponent(programId)}/users/${encodeURIComponent(userId)}/metadata`,
    },
    DIVISIONS: {
      ALL: (programId: string, businessUnitId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/divisions`,
      BY_ID: (id: number) => `/divisions/${encodeURIComponent(id)}`,
      CREATE: (programId: string, businessUnitId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/divisions`,
      UPDATE: (programId: string, businessUnitId: string, divisionId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/divisions/${encodeURIComponent(divisionId)}`,
      DELETE: (programId: string, businessUnitId: string, divisionId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/divisions/${encodeURIComponent(divisionId)}`,
    },
    ROLE: {
      ALL_BY_BUSINESS_UNIT: (programId: string, businessUnitId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/roles`,
      ALL_DEFAULT: (programId: string) =>
        `/programs/${encodeURIComponent(programId)}/roles`,

      UPDATE: (programId: string, roleId: string) =>
        `/programs/${encodeURIComponent(programId)}/roles/${encodeURIComponent(roleId)}`,
      UPDATE_BUSINESS_UNIT_ROLE: (
        programId: string,
        businessUnitId: string,
        roleId: string,
      ) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/roles/${encodeURIComponent(roleId)}`,
    },
    PERMISSION: {
      ALL: (programId: string) =>
        `/programs/${encodeURIComponent(programId)}/permissions`,
      BY_ID: (programId: string, permissionId: string) =>
        `/programs/${encodeURIComponent(programId)}/permissions/${encodeURIComponent(permissionId)}`,
      CREATE: (programId: string) =>
        `programs/${encodeURIComponent(programId)}/permissions`,
      UPDATE: (programId: string, permissionId: string) =>
        `/programs/${encodeURIComponent(programId)}/permissions/${encodeURIComponent(permissionId)}`,
      DELETE: (programId: string, permissionId: string) =>
        `/programs/${encodeURIComponent(programId)}/permissions/${encodeURIComponent(permissionId)}`,
    },
    BUSINESS_UNIT: {
      ALL: (programId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units`,
      CREATE: (programId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units`,
      DELETE: (programId: string, businessUnitId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}`,
      GET: (programId: string, businessUnitId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}`,
      UPDATE: (programId: string, businessUnitId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}`,
    },
    PREFERENCES: {
      GET: (programId: string, userId: string) =>
        `/programs/${encodeURIComponent(programId)}/users/${encodeURIComponent(userId)}/preferences`,
      UPDATE: (programId: string, userId: string) =>
        `/programs/${encodeURIComponent(programId)}/users/${encodeURIComponent(userId)}/preferences`,
    },
    PEOPLE: {
      SUGGEST: (programId: string) =>
        `/programs/${encodeURIComponent(programId)}/people/suggest`,
    },
  },
  HELIOS_SERVICE: {
    TAGS: {
      CREATE: (programId: string, businessUnitId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/tags`,
      UPDATE: (programId: string, businessUnitId: string, tagId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/tags/${encodeURIComponent(tagId)}`,
      DELETE: (programId: string, businessUnitId: string, tagId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/tags/${encodeURIComponent(tagId)}`,
      LIST: (programId: string, businessUnitId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/tags`,
      GET: (programId: string, businessUnitId: string, tagId: string) =>
        `/programs/${encodeURIComponent(programId)}/business-units/${encodeURIComponent(businessUnitId)}/tags/${encodeURIComponent(tagId)}`,
    },
    CATEGORIES: {
      LIST: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/categories`,
      CREATE: (businessUnitId: string, divisionName: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/divisions/${encodeURIComponent(divisionName)}/categories`,
      GET: (businessUnitId: string, divisionName: string, categoryName: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/divisions/${encodeURIComponent(divisionName)}/categories/${encodeURIComponent(categoryName)}`,
      UPDATE: (businessUnitId: string, divisionName: string, categoryName: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/divisions/${encodeURIComponent(divisionName)}/categories/${encodeURIComponent(categoryName)}`,
    },
    LOCATIONS: {
      LIST: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/locations`,
      CREATE: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/locations`,
      UPDATE: (businessUnitId: string, locationCode: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/locations/${encodeURIComponent(locationCode)}`,
      GET: (businessUnitId: string, locationCode: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/locations/${encodeURIComponent(locationCode)}`,
    },
    TEMPLATES: {
      ALL: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-templates`,
      BY_ID: (businessUnitId: string, templateName: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-templates/${encodeURIComponent(templateName)}`,
      CREATE: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-templates`,
      UPDATE: (businessUnitId: string, templateName: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-templates/${encodeURIComponent(templateName)}`,
      DELETE: (businessUnitId: string, templateName: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-templates/${encodeURIComponent(templateName)}`,
    },
    SCHEDULES: {
      ALL: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-schedules`,
      BY_ID: (businessUnitId: string, scheduleId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-schedules/${encodeURIComponent(scheduleId)}`,
      CREATE: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-schedules`,
      UPDATE: (businessUnitId: string, scheduleId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-schedules/${encodeURIComponent(scheduleId)}`,
      DELETE: (businessUnitId: string, scheduleId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audit-schedules/${encodeURIComponent(scheduleId)}`,
    },

    QUESTIONS: {
      SEARCH_QUESTIONS: (businessUnitId: string) =>
        `/business-unit/${encodeURIComponent(businessUnitId)}/search/questions`,
      ALL: (programId: string, businessUnit: string, divisionName: string) =>
        `/programs/${encodeURIComponent(programId)}/businessUnits/${encodeURIComponent(businessUnit)}/divisions/${encodeURIComponent(divisionName)}/questions`,
      CREATE: (programId: string, businessUnit: string, divisionName: string) =>
        `/programs/${encodeURIComponent(programId)}/businessUnits/${encodeURIComponent(businessUnit)}/divisions/${encodeURIComponent(divisionName)}/questions`,
      UPDATE: (
        programId: string,
        businessUnit: string,
        divisionName: string,
        questionId: string,
      ) =>
        `/programs/${encodeURIComponent(programId)}/businessUnits/${encodeURIComponent(businessUnit)}/divisions/${encodeURIComponent(divisionName)}/questions/${encodeURIComponent(questionId)}`,
      BY_ID: (
        programId: string,
        businessUnit: string,
        divisionName: string,
        questionId: string,
      ) =>
        `/programs/${encodeURIComponent(programId)}/businessUnits/${encodeURIComponent(businessUnit)}/divisions/${encodeURIComponent(divisionName)}/questions/${encodeURIComponent(questionId)}`,
      UPDATE_TRANSLATION: (
        programId: string,
        businessUnit: string,
        divisionName: string,
        questionId: string,
        language: string,
      ) =>
        `/programs/${encodeURIComponent(programId)}/businessUnits/${encodeURIComponent(businessUnit)}/divisions/${encodeURIComponent(divisionName)}/questions/${encodeURIComponent(questionId)}/translations/${encodeURIComponent(language)}`,
    },
    MEDIA: {
      UPLOAD: (programId: string) => `/programs/${encodeURIComponent(programId)}/media`,
      GET: (programId: string, mediaId: string) =>
        `/programs/${encodeURIComponent(programId)}/media/${encodeURIComponent(mediaId)}`,
      DELETE: (programId: string, mediaId: string) =>
        `/programs/${encodeURIComponent(programId)}/media/${encodeURIComponent(mediaId)}`,
      LIST: (programId: string) => `/programs/${encodeURIComponent(programId)}/media`,
      LIST_BY_IDS: (programId: string) =>
        `/programs/${encodeURIComponent(programId)}/batch-media`,
    },
    AUDITS: {
      LIST: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits`,
      CREATE: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits`,
      GET: (businessUnitId: string, auditId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}`,
      UPDATE: (businessUnitId: string, auditId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}`,
      REOPEN: (businessUnitId: string, auditId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}/reopen`,
      SEARCH: (businessUnitId: string) =>
        `/business-unit/${encodeURIComponent(businessUnitId)}/search/audits`,
      SUMMARY: (businessUnitId: string, auditId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}/summary`,
      STATS: (businessUnitId: string) =>
        `/business-unit/${encodeURIComponent(businessUnitId)}/search/audits/stats`,
    },
    QUESTION_RESPONSES: {
      LIST: (businessUnitId: string, auditId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}/questions`,
      CREATE: (businessUnitId: string, auditId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}/questions`,
      GET: (businessUnitId: string, auditId: string, questionId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}/questions/${encodeURIComponent(questionId)}`,
      UPDATE: (businessUnitId: string, auditId: string, questionId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}/questions/${encodeURIComponent(questionId)}/response`,
      DELETE: (businessUnitId: string, auditId: string, questionId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}/questions/${encodeURIComponent(questionId)}`,
      SEARCH: (businessUnitId: string, auditId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/audits/${encodeURIComponent(auditId)}/search/questions`,
    },
    TASKS: {
      LIST: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/tasks`,
      CREATE: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/tasks`,
      CREATE_FROM_QUESTION: (businessUnitId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/tasks/from-question`,
      GET: (businessUnitId: string, taskId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/tasks/${encodeURIComponent(taskId)}`,
      UPDATE_ASSIGNEE: (businessUnitId: string, taskId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/tasks/${encodeURIComponent(taskId)}/assignee`,
      AUDITOR_UPDATE: (businessUnitId: string, taskId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/tasks/${encodeURIComponent(taskId)}/auditor`,
      CANCEL: (businessUnitId: string, taskId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/tasks/${encodeURIComponent(taskId)}`,
      REOPEN: (businessUnitId: string, taskId: string) =>
        `/business-units/${encodeURIComponent(businessUnitId)}/tasks/${encodeURIComponent(taskId)}/reopen`,
      SEARCH: (businessUnitId: string) =>
        `/business-unit/${encodeURIComponent(businessUnitId)}/search/tasks`,
      STATS: (businessUnitId: string) =>
        `/business-unit/${encodeURIComponent(businessUnitId)}/search/tasks/stats`,
    },
  },
} as const;
